import React, { useRef } from 'react';
import { gsap, SplitText, useGSAP, prefersReducedMotion } from '../lib/gsap';
import { HeroCopy } from './Layout/nav';

const MARQUEE = ['Accountability', 'Memory', 'Evidence', 'Power', 'Record', 'Consequence'];

const Hero = ({ copy }: { copy: HeroCopy }) => {
   const root = useRef<HTMLElement>(null);
   const title = useRef<HTMLHeadingElement>(null);
   const xl = copy.size !== 'md';

   useGSAP(
      () => {
         if (prefersReducedMotion() || !title.current) return;
         const split = SplitText.create(title.current, {
            type: 'lines,words',
            mask: 'lines',
            linesClass: 'split-line',
            autoSplit: true,
            onSplit: self =>
               gsap.from(self.words, { yPercent: 115, rotate: 4, stagger: 0.045, duration: 1.4, delay: 0.1 }),
         });
         gsap.from('[data-hero-fade]', { autoAlpha: 0, y: 18, stagger: 0.12, duration: 1.2, delay: 0.45 });
         gsap.from('[data-hero-line]', { scaleX: 0, transformOrigin: 'left', duration: 1.6, delay: 0.3, ease: 'expo.inOut' });
         return () => split.revert();
      },
      { scope: root, dependencies: [copy.title], revertOnUpdate: true }
   );

   return (
      <section
         ref={root}
         className={`relative flex flex-col justify-end ${xl ? 'min-h-[78vh] md:min-h-[86vh]' : 'min-h-[46vh]'} pb-10 pt-28`}
      >
         <div className="page relative">
            <div data-hero-fade className="mb-6 flex items-center gap-3">
               <span className="h-px w-8 bg-signal" />
               <span className="eyebrow text-signal-soft">{copy.kicker}</span>
            </div>

            <h1
               ref={title}
               className={`max-w-5xl font-display leading-[0.92] tracking-tightest text-bone ${
                  xl ? 'text-[15vw] sm:text-7xl md:text-8xl xl:text-[8.5rem]' : 'text-6xl md:text-7xl'
               }`}
            >
               {copy.title}
            </h1>

            {copy.blurb && (
               <div className="mt-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                  <p data-hero-fade className="max-w-md text-base leading-relaxed text-bone-muted md:text-lg">
                     {copy.blurb}
                  </p>
                  <div data-hero-fade className="eyebrow hidden items-center gap-3 md:flex">
                     <span>Scroll</span>
                     <span className="relative block h-10 w-px overflow-hidden bg-white/10">
                        <span className="absolute inset-x-0 top-0 h-1/2 animate-scrollLine bg-signal" />
                     </span>
                  </div>
               </div>
            )}

            <div data-hero-line className="rule mt-10" />
         </div>

         {xl && (
            <div data-hero-fade className="relative mt-6 overflow-hidden whitespace-nowrap" aria-hidden="true">
               <div className="inline-flex animate-marquee gap-10 font-mono text-[11px] uppercase tracking-[0.4em] text-bone-dim">
                  {[...MARQUEE, ...MARQUEE, ...MARQUEE, ...MARQUEE].map((word, i) => (
                     <span key={i} className="flex items-center gap-10">
                        {word}
                        <span className="text-signal">✶</span>
                     </span>
                  ))}
               </div>
            </div>
         )}
      </section>
   );
};

export default Hero;
